<template>
  <article>
    <h1 class="text-h3" id="about" v-intersect="onIntersect">AlphaX Sky</h1>
    <p>Version {{ version }}</p>
    <p>&copy; AlphaX Decision Sciences. All rights reserved.</p>
    <p>
      <strong>Warning</strong>
      : This computer program is protected by copyright law and international treaties. Unauthorized reproduction or
      distribution of this program, or any portion of it, may result in severe civil and criminal penalties, and will be
      prosecuted to the maximum extent possible under the law.
    </p>
    <p>
      THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
      THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
      AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF
      CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
      IN THE SOFTWARE.
    </p>

    <h2 class="text-h4" id="terms" v-intersect="onIntersect">Terms of Service</h2>
    <p>
      These Terms of Service reflect the way that our business works, the laws that apply to our company, and certain
      things that we've always believed to be true. As a result, these Terms of Service help define our relationship
      with you as you interact with our services
    </p>

    <h3 class="text-h5" id="terms_intro" v-intersect="onIntersect">
      Introduction
    </h3>
    <ul>
      <li>
        This web application is a product of
        <strong>ALPHAX DECISION SCIENCES</strong>
        .
      </li>
      <li>
        When using this website, you are agreeing to all of these terms, and our
        <router-link :to="{ path: '/help/about', hash: '#privacy' }">Privacy Policy</router-link>
        on how we collect and store your personal data.
      </li>
    </ul>

    <h3 class="text-h5" id="obligations" v-intersect="onIntersect">
      Obligations
    </h3>
    <ul>
      <li>
        You agree that: (1) you are 10 years or older, (2) you'll have only one account in your own real name, (3)
        you're not already restricted from using our Services.
      </li>
      <li>
        You agree that: (1) you will keep your credentials secret, (2) you will not share your account with anyone else,
        and (3) you will follow our rules and the law.
      </li>
      <li>
        You're okay with us using our website and email to provide you with important notices. Also, you agree certain
        additional information can be shared with us.
      </li>
    </ul>

    <h3 class="text-h5" id="rights" v-intersect="onIntersect">
      Rights and Limits
    </h3>
    <ul>
      <li>
        You own all of the personal information you provide us, but you also grant us a non-exclusive license to it. Any
        personally identifying information will not be disclosed to any third party.
      </li>
      <li>
        We may change or discontinue any of our Services. We can't promise to store or keep showing any information and
        content you might have been part of.
      </li>
      <li>
        We have the right to limit how you connect and interact on our Services. We reserve all of our intellectual
        property rights in the Services.
      </li>
    </ul>

    <h3 class="text-h5" id="disclaimer" v-intersect="onIntersect">
      Disclaimer
    </h3>
    <ul>
      <li>
        To the extent permitted under law, we disclaim any and all implied warrantees and representations. We do not
        guarantee that the Service will function without interruption or errors. The Service (including content and
        information) is provided on an "AS IS" and "AS AVAILABLE" basis.
      </li>
      <li>
        To the extent permitted under law, we shall not be held liable to you or others for any damage, or loss, or
        anything else.
      </li>
    </ul>

    <h3 class="text-h5" id="miscellaneous" v-intersect="onIntersect">
      Miscellaneous
    </h3>
    <ul>
      <li>We can each end this Agreement anytime we want.</li>
      <li>
        We respect the intellectual property rights of others. We try to ensure that our content is not in violation of
        any intellectual property right of third parties.
      </li>
      <li>
        In the unlikely event we end up in a legal dispute, it will take place in California courts, applying U.S. law.
      </li>
    </ul>

    <h2 class="text-h4" id="privacy" v-intersect="onIntersect">Privacy Policy</h2>
    <p>
      This Privacy Policy is meant to help you understand what information we collect, why we collect it and how you can
      update, manage, export and delete your information.
    </p>

    <h3 class="text-h5" id="privacy_intro" v-intersect="onIntersect">
      Introduction
    </h3>
    <ul>
      <li>
        This is a web application of
        <strong>ALPHAX DECISION SCIENCES</strong>
        . We protect your personal information using industry-standard safeguards.
      </li>
      <li>
        We may share your information with your consent or as required by law, and we will always let you know when we
        make significant changes to this privacy policy.
      </li>
    </ul>

    <h3 class="text-h5" id="information" v-intersect="onIntersect">
      Information we collect
    </h3>
    <ul>
      <li>
        When you create an account with us, we collect information (including your name and email address).
      </li>
      <li>
        We collect information when you fill out our registration forms. A complete form including personal details ends
        up as statistics in our services.
      </li>
      <li>We use cookies and similar technologies to collect information.</li>
      <li>
        We collect information from the devices and networks that you use to access our Service.
      </li>
    </ul>

    <h3 class="text-h5" id="usage" v-intersect="onIntersect">
      How personal information is used
    </h3>
    <ul>
      <li>
        You agree that information you provide on your profile can be seen used by us as described in this privacy
        policy and our
        <router-link :to="{ path: '/help/about', hash: '#terms' }">Terms of service</router-link>
        .
      </li>
      <li>
        We communicate with you using email and SMS. We may send you messages relating to the availability of the
        Service, security, or other service-related issues.
      </li>
      <li>
        We use the information you provide to us to conduct analysis, research and development, and to customize your
        experience and try to make it relevant and useful to you.
      </li>
      <li>
        We do not share any personally identifying information that you provide to us. Non-identifying information is
        publicly shared as part of our Service.
      </li>
      <li>
        We work with developers to build applications using our developer tools. If you have given an application access
        to your account, you can revoke that permission anytime. Also, you can opt out of providing information to
        developers through your connections.
      </li>
      <li>
        We conduct our own surveys and polls and also help third parties do this type of research. Your participation in
        surveys or polls is up to you. You may also opt out of getting invitations to participate in surveys.
      </li>
      <li>
        We may disclose your personal information if compelled by law, subpoena, or other legal process, or if necessary
        to enforce our User Agreement.
      </li>
      <li>
        We may process your information outside the country where you live.
      </li>
    </ul>

    <h3 class="text-h5" id="choices" v-intersect="onIntersect">
      Choices and Obligations
    </h3>
    <ul>
      <li>
        You can change your account information at any time by editing your profile, or by closing your account. You can
        also ask us for additional information we may have about your account.
      </li>
      <li>
        We keep your information for as long as your account is active or as needed. We may keep certain information
        even after you close your account if it is necessary to comply with our legal obligations, meet regulatory
        requirements, resolve disputes, prevent fraud and abuse, or enforce this agreement.
      </li>
    </ul>

    <h3 class="text-h5" id="important" v-intersect="onIntersect">
      Important Information
    </h3>
    <ul>
      <li>
        You have to meet our minimum age requirements to create an account.
      </li>
      <li>
        We don't share any of your personal information with third parties for direct marketing.
      </li>
      <li>
        We take privacy and security seriously and have enabled HTTPS access to our site, in addition to existing SSL
        access over mobile devices. Also, please know that the Internet is not a secure environment, so be careful and
        select strong passwords.
      </li>
    </ul>

    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpAbout",
  components: { Contents },
  data() {
    return {
      selected: "intro",
      menu: [
        { id: "about", text: "About AlphaX Sky", depth: 1 },
        { id: "terms", text: "Terms of service", depth: 1 },
        { id: "terms_intro", text: "Introduction", depth: 2 },
        { id: "obligations", text: "Obligations", depth: 2 },
        { id: "disclaimer", text: "Disclaimer", depth: 2 },
        { id: "miscellaneous", text: "Miscellaneous", depth: 2 },
        { id: "privacy", text: "Privacy policy", depth: 1 },
        { id: "privacy_intro", text: "Introduction", depth: 2 },
        { id: "information", text: "Information we collect", depth: 2 },
        { id: "usage", text: "How personal information is used", depth: 2 },
        { id: "choices", text: "Choices and obligations", depth: 2 },
        { id: "information", text: "Important information", depth: 2 }
      ]
    }
  },
  computed: {
    version() {
      return process.env.VUE_APP_VERSION
    }
  },
  methods: {
    onIntersect(entries, observer) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          let target = entry.target.id
          this.$refs.contents.selected = target
        }
      }
    }
  }
}
</script>
